import React, { useEffect, useMemo, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { useHistory } from "react-router-dom";
import FlashAlert from "../../Components/FlashAlert/FlashAlert";
import apiService from "../../services/api.service";
import {
  filterIscrizioni,
  isEmailVerified,
  iscrittiCountLabel,
} from "../../utils/iscrizioniFilters";

const displayName = (user) => {
  const name = `${user.first_name || ""} ${user.last_name || ""}`.trim();
  return name || user.username;
};

const DashboardUsersByApplication = () => {
  const history = useHistory();
  const [applications, setApplications] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [applicationId, setApplicationId] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const [appsRes, usersRes] = await Promise.all([
          apiService.getApplications(),
          apiService.getIscrizioni(),
        ]);
        setApplications(appsRes.data || []);
        setUsers(usersRes.data || []);
      } catch {
        setError("Impossibile caricare gli utenti.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const appsById = useMemo(
    () => Object.fromEntries(applications.map((app) => [String(app.id), app])),
    [applications]
  );

  const filteredUsers = useMemo(
    () => filterIscrizioni(users, { applicationId }),
    [users, applicationId]
  );

  const applicationLabel = (user) => {
    const app = appsById[String(user.application_id)];
    return app ? app.name : "Senza applicazione";
  };

  return (
    <div className="dashboard-card">
      <div className="dashboard-card__header">
        <div>
          <h1 className="dashboard-card__title">Utenti</h1>
          <p className="dashboard-card__subtitle">
            Utenti comuni iscritti alle applicazioni.
          </p>
        </div>
        <div className="events-header-actions">
          {!loading && (
            <span className="dashboard-count">{iscrittiCountLabel(filteredUsers.length)}</span>
          )}
          <Button
            variant="primary"
            onClick={() => history.push("/dashboard/utenti/nuovo")}
          >
            Nuovo utente
          </Button>
        </div>
      </div>

      {error && <FlashAlert variant="danger" onHidden={() => setError("")}>{error}</FlashAlert>}
      {loading && <p>Caricamento...</p>}

      {!loading && (
        <div className="dashboard-iscrizioni-filters dashboard-iscrizioni-filters--single">
          <Form.Group controlId="utenti-applicazione">
            <Form.Label>Applicazione</Form.Label>
            <Form.Select
              value={applicationId}
              onChange={(e) => setApplicationId(e.target.value)}
              aria-label="Filtra per applicazione"
            >
              <option value="">Tutte</option>
              {applications.map((app) => (
                <option key={app.id} value={app.id}>{app.name}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>
      )}

      {!loading && users.length === 0 && !error && (
        <Alert variant="info">Nessun utente iscritto.</Alert>
      )}

      {!loading && users.length > 0 && filteredUsers.length === 0 && (
        <Alert variant="info">Nessun iscritto corrisponde ai filtri selezionati.</Alert>
      )}

      {!loading && filteredUsers.length > 0 && (
        <div className="dashboard-events-list">
          {filteredUsers.map((user) => (
            <div className="dashboard-event-item" key={user.id}>
              <div>
                <p className="dashboard-event-item__title">{displayName(user)}</p>
                <p className="dashboard-event-item__meta">{user.username}</p>
                <p className="dashboard-event-item__meta">{applicationLabel(user)}</p>
                {(user.provincia || user.croas) && (
                  <p className="dashboard-event-item__meta">
                    {[user.provincia, user.croas].filter(Boolean).join(" · ")}
                  </p>
                )}
                <p
                  className={`dashboard-iscrizione-status${
                    isEmailVerified(user) ? " dashboard-iscrizione-status--verified" : ""
                  }`}
                >
                  {isEmailVerified(user) ? "Email verificata" : "Email non verificata"}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DashboardUsersByApplication;
